import React from "react";
import { Trash2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { safeFrameCode } from "@/lib/frameCode";

/** The client's submitted selection, with admin-editable print size and frame code. */
export const SelectionSummary = ({ summary, printSizes, onUpdatePrint, onRemovePrint, onAddPrint }) => {
  const { t } = useI18n();
  const s = summary;
  const sizes = printSizes || [];

  return (
    <section data-testid="admin-selection-summary">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <p className="eyebrow">{t("client_selection")}</p>
        <p className="text-[11px] text-[#707070]">{t("admin_override_hint")}</p>
      </div>
      <div className="border border-[#2A2A2A] bg-[#0B0B0B] divide-y divide-[#1E1E1E]">
        {s.edits.map((item) => {
          const prints = s.prints.filter((p) => p.file_id === item.file_id);
          return (
            <div key={item.file_id} className="flex flex-col sm:flex-row gap-5 p-5">
              <img src={item.thumb} alt={item.name} className="w-full sm:w-24 h-40 sm:h-24 object-cover border border-[#2A2A2A]" />
              <div className="min-w-0 flex-1">
                <p className="font-mono-accent text-xs">
                  {item.name}
                  {item.is_extra && <span className="ml-2 text-[#D4AF37]">({t("extra")})</span>}
                </p>
                {item.note && <p className="mt-2 text-sm text-[#A8A8A8]">{t("notes")}: {item.note}</p>}
                {prints.map((p, i) => (
                  <div key={`${p.file_id}-${i}`} className="mt-3 flex flex-wrap items-center gap-3">
                    <select data-testid={`admin-print-size-${p.file_id}-${i}`} value={p.size}
                      onChange={(e) => onUpdatePrint(p.file_id, i, { size: e.target.value })}
                      className="bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-[#F3F3F1] appearance-none focus:border-[#D4AF37] focus:outline-none cursor-pointer"
                      style={{ backgroundImage: "none" }}>
                      {sizes.map((sz) => <option key={sz} value={sz} label={sz} />)}
                    </select>
                    <input data-testid={`admin-print-qty-${p.file_id}-${i}`} type="number" min="1" max="99"
                      defaultValue={p.qty || 1}
                      onBlur={(e) => onUpdatePrint(p.file_id, i, { qty: Math.max(1, Number(e.target.value) || 1) })}
                      aria-label={t("print_qty")}
                      className="w-16 bg-transparent border-b border-[#2A2A2A] px-0 py-1.5 text-xs focus:border-[#D4AF37] focus:outline-none" />
                    <input data-testid={`admin-frame-code-${p.file_id}-${i}`} defaultValue={p.frame_code}
                      maxLength={40}
                      onBlur={(e) => onUpdatePrint(p.file_id, i, { frame_code: safeFrameCode(e.target.value) })}
                      placeholder={t("frame_code")}
                      className="bg-transparent border-b border-[#2A2A2A] px-0 py-1.5 text-xs focus:border-[#D4AF37] focus:outline-none" />
                    <button data-testid={`admin-remove-print-${p.file_id}-${i}`} onClick={() => onRemovePrint(p.file_id, i)}
                      aria-label={t("remove_print")} className="text-[#5A5A5A] hover:text-[#8B0000]"><Trash2 size={13} /></button>
                  </div>
                ))}
                {sizes.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <select data-testid={`admin-add-print-select-${item.file_id}`} defaultValue=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        onAddPrint(item.file_id, e.target.value);
                        e.target.value = "";
                      }}
                      className="bg-[#050505] border border-[#D4AF37]/40 px-3 py-2 min-h-[40px] text-[10px] tracking-[0.14em] uppercase text-[#D4AF37] appearance-none focus:border-[#D4AF37] focus:outline-none cursor-pointer"
                      style={{ backgroundImage: "none" }}>
                      <option value="" label={`+ ${t("add_print")}`} />
                      {sizes.map((sz) => <option key={sz} value={sz} label={sz} />)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
